import {
  Alert,
  Card,
  Drawer,
  Fade,
  Grid,
  Icon,
  Modal,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import { gql, useQuery } from "@apollo/client";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";

export const GET_INFORMACION_NUTRICIONAL_PRODUCTO = gql`
  query informacionNutricionalProducto($idProducto: ID!) {
    informacionNutricionalProducto(idProducto: $idProducto) {
      disponible
      mensaje
      productoId
      productoNombre
      titulo
      cantidadPorciones
      completo
      secciones {
        codigo
        titulo
        disponible
        mensaje
        destacados {
          codigo
          etiqueta
          valor
          unidad
          completo
          valorFormateado
        }
        detalle {
          codigo
          etiqueta
          valor
          unidad
          completo
          valorFormateado
        }
      }
      aptitudes {
        codigo
        etiqueta
        estado
        texto
      }
      advertencias {
        codigo
        mensaje
        texto
        ingredienteIds
        ingredientes
      }
      explicacion {
        titulo
        origen
        procedimiento
        tituloConversiones
        conversiones
        tituloCalculosPrincipales
        notaCalculosPrincipales
        calculosPrincipales {
          codigo
          titulo
          resultadoFormateado
          tieneDatos
          completo
          aportes {
            ingrediente
            completo
            texto
          }
          sumaFormateada
          porPorcionFormateado
        }
        aclaracion
      }
    }
  }
`;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "94%", sm: "86%", md: "68%", lg: "58%" },
  maxHeight: "90vh",
  overflowY: "auto",
  p: { xs: 2, sm: 3 },
};

const nutrientIcons = {
  CALORIAS: "local_fire_department",
  GRASAS_TOTALES: "water_drop",
  CARBOHIDRATOS_TOTALES: "bakery_dining",
  PROTEINAS: "restaurant",
};

const aptitudePresentation = {
  APTO: { color: "uproGreen", icon: "done" },
  NO_APTO: { color: "primary", icon: "close" },
  DESCONOCIDO: { color: "uproYellow", icon: "help_outline" },
};

const LoadingContent = () => (
  <Grid container spacing={2}>
    {[0, 1, 2, 3].map((item) => (
      <Grid item xs={12} sm={6} md={3} key={item}>
        <Card>
          <SoftBox p={2}>
            <Skeleton variant="text" width="55%" />
            <Skeleton variant="text" height={44} width="85%" />
          </SoftBox>
        </Card>
      </Grid>
    ))}
    <Grid item xs={12}>
      <Skeleton variant="rounded" height={180} />
    </Grid>
  </Grid>
);

const ExplanationTrigger = ({ explanation, buttonRef, onOpen }) => (
  <Card>
    <SoftBox
      component="button"
      type="button"
      ref={buttonRef}
      width="100%"
      p={2}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      gap={2}
      onClick={onOpen}
      aria-haspopup="dialog"
      sx={({ palette }) => ({
        appearance: "none",
        border: 0,
        background: "transparent",
        color: "inherit",
        cursor: "pointer",
        textAlign: "left",
        transition: "background-color 200ms ease",
        "&:hover": { backgroundColor: palette.grey[100] },
        "&:focus-visible": {
          outline: `2px solid ${palette.primary.main}`,
          outlineOffset: "-2px",
        },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      })}
    >
      <SoftBox display="flex" alignItems="center" gap={1.25}>
        <Icon color="primary">info_outline</Icon>
        <SoftTypography variant="h6">{explanation.titulo}</SoftTypography>
      </SoftBox>
      <Icon aria-hidden="true">arrow_forward</Icon>
    </SoftBox>
  </Card>
);

ExplanationTrigger.propTypes = {
  explanation: PropTypes.shape({ titulo: PropTypes.string.isRequired }).isRequired,
  buttonRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  onOpen: PropTypes.func.isRequired,
};

const ExplanationDrawer = ({
  explanation,
  complete,
  open,
  containerRef,
  backButtonRef,
  onBack,
}) => (
  <Drawer
    anchor="right"
    variant="temporary"
    open={open}
    onClose={onBack}
    container={() => containerRef.current}
    SlideProps={{ timeout: 250 }}
    ModalProps={{ disablePortal: true }}
    PaperProps={{
      component: "section",
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": "nutrition-explanation-title",
    }}
    sx={{
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      zIndex: 1400,
      pointerEvents: open ? "auto" : "none",
      "& .MuiDrawer-paper": {
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        maxHeight: "100%",
        margin: 0,
        border: 0,
        borderRadius: "inherit",
        backgroundColor: "#fff",
        overflow: "hidden",
      },
      "@media (prefers-reduced-motion: reduce)": {
        "& .MuiDrawer-paper": { transition: "none !important" },
      },
    }}
  >
    <SoftBox height="100%" display="flex" flexDirection="column" bgColor="white">
      <SoftBox
        px={{ xs: 2, sm: 3 }}
        py={2}
        display="flex"
        alignItems="center"
        gap={1.5}
        borderBottom="1px solid"
        borderColor="grey.200"
        flexShrink={0}
      >
        <SoftButton
          ref={backButtonRef}
          autoFocus
          variant="text"
          color="dark"
          onClick={onBack}
          aria-label="Volver a la información nutricional"
          sx={{ px: 1.25 }}
        >
          <Icon>arrow_back</Icon>&nbsp; Volver
        </SoftButton>
        <SoftTypography
          id="nutrition-explanation-title"
          variant="h6"
          sx={{ minWidth: 0, whiteSpace: "normal", lineHeight: 1.35 }}
        >
          {explanation.titulo}
        </SoftTypography>
      </SoftBox>

      <SoftBox
        px={{ xs: 2, sm: 3 }}
        py={2.5}
        flex={1}
        minHeight={0}
        display="flex"
        flexDirection="column"
        gap={2.5}
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          "& > *": { flexShrink: 0, minWidth: 0 },
          "& .MuiTypography-root": {
            whiteSpace: "normal",
            overflowWrap: "anywhere",
          },
          "& .MuiAlert-root, & .MuiAlert-message, & .MuiCard-root": {
            minWidth: 0,
          },
          "& .MuiAlert-message": {
            whiteSpace: "normal",
            overflowWrap: "anywhere",
          },
        }}
      >
        <SoftTypography variant="body2">{explanation.origen}</SoftTypography>

        <SoftBox component="ol" m={0} pl={2.5} display="flex" flexDirection="column" gap={1}>
          {explanation.procedimiento.map((step) => (
            <SoftTypography component="li" variant="body2" key={step}>
              {step}
            </SoftTypography>
          ))}
        </SoftBox>

        {explanation.conversiones.length > 0 && (
          <Card variant="outlined" sx={{ flexShrink: 0 }}>
            <SoftBox p={2}>
              <SoftTypography variant="subtitle2" fontWeight="bold" mb={1}>
                {explanation.tituloConversiones}
              </SoftTypography>
              <SoftBox component="ul" m={0} pl={2.5} display="flex" flexDirection="column" gap={1}>
                {explanation.conversiones.map((conversion) => (
                  <SoftTypography component="li" variant="body2" key={conversion}>
                    {conversion}
                  </SoftTypography>
                ))}
              </SoftBox>
            </SoftBox>
          </Card>
        )}

        <SoftBox>
          <SoftTypography variant="h6" mb={0.5}>
            {explanation.tituloCalculosPrincipales}
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mb={2}>
            {explanation.notaCalculosPrincipales}
          </SoftTypography>

          <SoftBox display="flex" flexDirection="column" gap={2}>
            {explanation.calculosPrincipales.map((calculation) => (
              <Card key={calculation.codigo} variant="outlined" sx={{ flexShrink: 0 }}>
                <SoftBox p={{ xs: 1.75, sm: 2 }} display="flex" flexDirection="column" gap={1.5}>
                  <SoftBox
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    gap={0.75}
                  >
                    <SoftTypography variant="subtitle1" fontWeight="bold">
                      {calculation.titulo}
                    </SoftTypography>
                    <SoftTypography
                      variant="h6"
                      color={calculation.completo ? "primary" : "warning"}
                    >
                      {calculation.resultadoFormateado}
                    </SoftTypography>
                  </SoftBox>

                  {calculation.aportes.length > 0 && (
                    <SoftBox
                      component="ol"
                      m={0}
                      pl={2.5}
                      display="flex"
                      flexDirection="column"
                      gap={1}
                    >
                      {calculation.aportes.map((contribution) => (
                        <SoftTypography
                          component="li"
                          variant="body2"
                          color={contribution.completo ? "dark" : "warning"}
                          key={`${contribution.ingrediente}-${contribution.texto}`}
                        >
                          {contribution.texto}
                        </SoftTypography>
                      ))}
                    </SoftBox>
                  )}

                  <Alert severity={calculation.completo ? "info" : "warning"}>
                    {calculation.sumaFormateada}
                  </Alert>
                  {calculation.porPorcionFormateado && (
                    <SoftTypography variant="body2" fontWeight="medium">
                      {calculation.porPorcionFormateado}
                    </SoftTypography>
                  )}
                </SoftBox>
              </Card>
            ))}
          </SoftBox>
        </SoftBox>

        <Alert severity={complete ? "success" : "warning"}>{explanation.aclaracion}</Alert>
      </SoftBox>
    </SoftBox>
  </Drawer>
);

ExplanationDrawer.propTypes = {
  explanation: PropTypes.shape({
    titulo: PropTypes.string.isRequired,
    origen: PropTypes.string.isRequired,
    procedimiento: PropTypes.arrayOf(PropTypes.string).isRequired,
    tituloConversiones: PropTypes.string.isRequired,
    conversiones: PropTypes.arrayOf(PropTypes.string).isRequired,
    tituloCalculosPrincipales: PropTypes.string.isRequired,
    notaCalculosPrincipales: PropTypes.string.isRequired,
    calculosPrincipales: PropTypes.arrayOf(
      PropTypes.shape({
        codigo: PropTypes.string.isRequired,
        titulo: PropTypes.string.isRequired,
        resultadoFormateado: PropTypes.string.isRequired,
        tieneDatos: PropTypes.bool.isRequired,
        completo: PropTypes.bool.isRequired,
        aportes: PropTypes.arrayOf(
          PropTypes.shape({
            ingrediente: PropTypes.string.isRequired,
            completo: PropTypes.bool.isRequired,
            texto: PropTypes.string.isRequired,
          })
        ).isRequired,
        sumaFormateada: PropTypes.string.isRequired,
        porPorcionFormateado: PropTypes.string,
      })
    ).isRequired,
    aclaracion: PropTypes.string.isRequired,
  }).isRequired,
  complete: PropTypes.bool.isRequired,
  open: PropTypes.bool.isRequired,
  containerRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  backButtonRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  onBack: PropTypes.func.isRequired,
};

export default function ModalInformacionNutricional({ open, handleClose, idProducto }) {
  const [sectionCode, setSectionCode] = useState("RECETA_COMPLETA");
  const [explanationOpen, setExplanationOpen] = useState(false);
  const modalCardRef = useRef(null);
  const explanationTriggerRef = useRef(null);
  const explanationBackButtonRef = useRef(null);
  const restoreExplanationFocusRef = useRef(false);
  const { loading, error, data } = useQuery(GET_INFORMACION_NUTRICIONAL_PRODUCTO, {
    variables: { idProducto },
    fetchPolicy: "no-cache",
    skip: !open || !idProducto,
  });

  useEffect(() => {
    setSectionCode("RECETA_COMPLETA");
    setExplanationOpen(false);
    restoreExplanationFocusRef.current = false;
  }, [idProducto, open]);

  useEffect(() => {
    if (explanationOpen) {
      explanationBackButtonRef.current?.focus();
      return;
    }

    if (restoreExplanationFocusRef.current) {
      explanationTriggerRef.current?.focus();
      restoreExplanationFocusRef.current = false;
    }
  }, [explanationOpen]);

  const handleExplanationBack = () => {
    restoreExplanationFocusRef.current = true;
    setExplanationOpen(false);
  };

  const nutrition = !loading && !error ? data?.informacionNutricionalProducto : null;
  const activeSection = nutrition?.secciones?.find((section) => section.codigo === sectionCode);

  return (
    <Modal
      aria-labelledby="nutrition-modal-title"
      aria-describedby="nutrition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card
          ref={modalCardRef}
          sx={{
            ...style,
            overflowY: explanationOpen ? "hidden" : "auto",
            overflowX: "hidden",
          }}
        >
          <SoftBox pb={2} display="flex" justifyContent="space-between" alignItems="center" gap={2}>
            <SoftTypography id="nutrition-modal-title" variant="h6">
              {nutrition?.titulo || "Información nutricional"}
            </SoftTypography>
            <Tooltip title="Cerrar" placement="top">
              <Icon
                fontSize="medium"
                onClick={handleClose}
                tabIndex={0}
                role="button"
                aria-label="Cerrar información nutricional"
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") handleClose();
                }}
                sx={{ cursor: "pointer" }}
              >
                close
              </Icon>
            </Tooltip>
          </SoftBox>

          {loading ? (
            <LoadingContent />
          ) : error ? (
            <Alert severity="error">
              No se pudo cargar la información nutricional. Cerrá el modal e intentá nuevamente.
            </Alert>
          ) : !nutrition?.disponible ? (
            <Alert severity="info">{nutrition?.mensaje || "Información no disponible."}</Alert>
          ) : (
            <SoftBox id="nutrition-modal-description">
              {!nutrition.completo && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {nutrition.mensaje}
                </Alert>
              )}

              <Tabs
                value={activeSection?.codigo || false}
                onChange={(event, value) => setSectionCode(value)}
                aria-label="Base del cálculo nutricional"
                variant="fullWidth"
                sx={{ mb: 2 }}
              >
                {nutrition.secciones.map((section) => (
                  <Tab
                    key={section.codigo}
                    value={section.codigo}
                    label={section.titulo}
                    disabled={!section.disponible}
                  />
                ))}
              </Tabs>

              {activeSection?.disponible ? (
                <Grid container spacing={2}>
                  {activeSection.destacados.map((nutrient) => (
                    <Grid item xs={12} sm={6} md={3} key={nutrient.codigo}>
                      <Card sx={{ height: "100%" }}>
                        <SoftBox p={2} display="flex" flexDirection="column" alignItems="center">
                          <Icon color="primary" fontSize="large">
                            {nutrientIcons[nutrient.codigo] || "nutrition"}
                          </Icon>
                          <SoftTypography variant="caption" fontWeight="bold" textAlign="center">
                            {nutrient.etiqueta}
                          </SoftTypography>
                          <SoftTypography
                            variant="h5"
                            fontWeight="bold"
                            color={nutrient.completo ? "primary" : "secondary"}
                            textAlign="center"
                          >
                            {nutrient.valorFormateado}
                          </SoftTypography>
                        </SoftBox>
                      </Card>
                    </Grid>
                  ))}

                  <Grid item xs={12}>
                    <SoftBox display="flex" flexDirection={{ xs: "column", md: "row" }} gap={1.5}>
                      {nutrition.aptitudes.map((aptitude) => {
                        const presentation =
                          aptitudePresentation[aptitude.estado] || aptitudePresentation.DESCONOCIDO;
                        return (
                          <SoftButton
                            key={aptitude.codigo}
                            color={presentation.color}
                            circular
                            fullWidth
                            sx={{ pointerEvents: "none" }}
                          >
                            <Icon>{presentation.icon}</Icon>
                            &nbsp; {aptitude.texto}
                          </SoftButton>
                        );
                      })}
                    </SoftBox>
                  </Grid>

                  <Grid item xs={12}>
                    <Card>
                      <SoftBox p={2} display="flex" flexDirection="column" gap={1.5}>
                        {activeSection.detalle.map((nutrient) => (
                          <SoftBox
                            key={nutrient.codigo}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            gap={2}
                          >
                            <SoftTypography variant="h6">{nutrient.etiqueta}</SoftTypography>
                            <SoftTypography
                              variant="h6"
                              color={nutrient.completo ? "dark" : "secondary"}
                              textAlign="right"
                            >
                              {nutrient.valorFormateado}
                            </SoftTypography>
                          </SoftBox>
                        ))}
                      </SoftBox>
                    </Card>
                  </Grid>

                  {nutrition.advertencias.length > 0 && (
                    <Grid item xs={12}>
                      <SoftBox display="flex" flexDirection="column" gap={1}>
                        {nutrition.advertencias.map((warning) => (
                          <Alert key={`${warning.codigo}-${warning.mensaje}`} severity="warning">
                            {warning.texto}
                          </Alert>
                        ))}
                      </SoftBox>
                    </Grid>
                  )}

                  {nutrition.explicacion && (
                    <Grid item xs={12}>
                      <ExplanationTrigger
                        explanation={nutrition.explicacion}
                        buttonRef={explanationTriggerRef}
                        onOpen={() => setExplanationOpen(true)}
                      />
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Alert severity="info">{activeSection?.mensaje || "Sección no disponible."}</Alert>
              )}
            </SoftBox>
          )}

          {nutrition?.explicacion && (
            <ExplanationDrawer
              explanation={nutrition.explicacion}
              complete={nutrition.completo}
              open={explanationOpen}
              containerRef={modalCardRef}
              backButtonRef={explanationBackButtonRef}
              onBack={handleExplanationBack}
            />
          )}
        </Card>
      </Fade>
    </Modal>
  );
}

ModalInformacionNutricional.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  idProducto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
